import { getDefaultSubdomain } from "@app/redux/data/config";
import { getState } from "@app/redux/utils";
import constants from "@app/utils/constants";
import {
  getAppUrl,
  getSubdomain,
  isAppInIframe,
  isValidAlias,
} from "@app/utils/helpers";
import queryString from "query-string";

const { DEFAULT_LOGIN_URL } = constants;

/**
 * Redirects the user to the external login page (Auth0) via a BFF, where it looks
 * up the login url using the company alias (subdomain).
 *
 * Two required query params are the `alias` and the `appUrl`. The alias helps in
 * looking up the company data, in case there is a configured OpenID configuration.
 * The base appUrl ensures a proper redirect-uri is provided to Auth0.
 */
export function getLoginRedirectUrl(params?: {
  email?: string;
  alias?: string;
}) {
  const email = params?.email;
  const forcedAlias = params?.alias;
  const isIframeAuth = isAppInIframe();
  const { protocol, host, pathname, search } = window.location;
  const redirectUrlPath = "/api/login/redirect";

  const appUrl = `${protocol}//${host}`;

  const subdomain = getSubdomain();
  const defaultSubdomain = getDefaultSubdomain(getState());

  const alias = isValidAlias(subdomain, defaultSubdomain)
    ? subdomain
    : forcedAlias || "";
  const hasRequiredParams = alias || email;

  const isLogin = pathname === DEFAULT_LOGIN_URL;

  const parsedQuery = queryString.parse(search);
  const returnTo = isLogin
    ? typeof parsedQuery.returnTo === "string" && parsedQuery.returnTo
      ? parsedQuery.returnTo
      : "/"
    : `${pathname}${search}`;

  let defaultLoginUrl = DEFAULT_LOGIN_URL;
  const urlParams: Record<string, string> = {};

  if (returnTo && returnTo !== "/") {
    urlParams.returnTo = returnTo;
  }
  if (isLogin && !hasRequiredParams) {
    urlParams.error = "true";
  }
  const defaultQuery = queryString.stringify(urlParams);
  if (defaultQuery) {
    defaultLoginUrl += `?${defaultQuery}`;
  }

  const loginUrl = hasRequiredParams
    ? `${appUrl}${redirectUrlPath}?${queryString.stringify({
        appUrl, // e.g., https://app.legalsiftercontrol.com
        alias, // e.g., app or datadog.app (or not defined if email is present)
        email,
        returnTo, // user to be redirected here post-login
        uuid: crypto.randomUUID(), // used as the state value for authentication with auth0 and maps to session information stored in the bff
        isIframeAuth,
      })}`
    : defaultLoginUrl.toString();

  if (isIframeAuth) {
    return `/iframe-login?${queryString.stringify({ loginUrl })}`;
  }

  return loginUrl;
}

export function getLogoutRedirectUrl() {
  const isIframeAuth = isAppInIframe();

  const returnTo = `${getAppUrl()}/logout?${queryString.stringify({
    isIframeAuth,
  })}`;

  const logoutUrl = `/api/login/logout/redirect?${queryString.stringify({
    returnTo,
  })}`;

  return logoutUrl;
}
